import { useMemo, useState } from 'react';
import { ChevronsLeft, Plus } from 'lucide-react';
import { cn } from '@clarix/ui';
import { SearchInput } from '../SearchInput';
import type { HomeChatItem } from './types';

export interface HomeChatListProps {
  chats: readonly HomeChatItem[];
  activeChatId?: string;
  onSelectChat?: (chat: HomeChatItem) => void;
  onNewChat?: () => void;
  /** Panel heading. */
  title?: string;
  newChatLabel?: string;
  searchPlaceholder?: string;
  /** Hides the panel; the parent renders the re-open affordance. */
  collapsed?: boolean;
  onCollapse?: () => void;
  className?: string;
}

/**
 * The conversation-history panel beside the home surface — same light look
 * as the shell's hover panels. Rows group under their `group` heading in the
 * order the groups first appear.
 */
export function HomeChatList({
  chats,
  activeChatId,
  onSelectChat,
  onNewChat,
  title = 'Chats',
  newChatLabel = 'New chat',
  searchPlaceholder = 'Search chats…',
  collapsed = false,
  onCollapse,
  className,
}: HomeChatListProps) {
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = q ? chats.filter((c) => c.title.toLowerCase().includes(q)) : chats;
    const byGroup = new Map<string, HomeChatItem[]>();
    for (const row of rows) {
      const bucket = byGroup.get(row.group);
      if (bucket) bucket.push(row);
      else byGroup.set(row.group, [row]);
    }
    return [...byGroup.entries()];
  }, [chats, query]);

  return (
    <aside
      aria-label={title}
      aria-hidden={collapsed}
      className={cn(
        'bg-surface border-border ease-spring flex shrink-0 flex-col overflow-hidden border-r transition-[width,opacity] duration-300',
        collapsed ? 'w-0 border-r-0 opacity-0' : 'w-[232px] opacity-100',
        className,
      )}
    >
      <div className="flex items-center gap-2 px-3.5 pb-2 pt-3.5">
        <span className="text-foreground flex-1 text-sm font-bold tracking-tight">{title}</span>
        {onCollapse ? (
          <button
            type="button"
            onClick={onCollapse}
            title="Collapse"
            aria-label="Collapse chats"
            className="text-muted-foreground hover:bg-muted hover:text-foreground grid h-[26px] w-[26px] shrink-0 place-items-center rounded-md transition-colors"
          >
            <ChevronsLeft className="h-[15px] w-[15px]" strokeWidth={1.5} />
          </button>
        ) : null}
      </div>

      <div className="px-3 pb-2">
        <SearchInput
          value={query}
          onChange={setQuery}
          size="sm"
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          debounce={0}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {onNewChat ? (
          <button
            type="button"
            onClick={onNewChat}
            className="hover:bg-muted hover:text-primary mx-2 mb-1 flex h-8 w-[calc(100%-1rem)] items-center justify-center gap-1.5 rounded-md text-xs font-semibold text-[rgb(var(--ink-2))] transition-colors"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
            {newChatLabel}
          </button>
        ) : null}

        {groups.map(([group, rows]) => (
          <div key={group}>
            <div className="text-muted-foreground px-2 pb-1 pt-3 text-[10px] font-bold uppercase tracking-[0.06em]">
              {group}
            </div>
            {rows.map((chat) => (
              <button
                key={chat.id}
                type="button"
                onClick={() => onSelectChat?.(chat)}
                aria-current={chat.id === activeChatId ? 'true' : undefined}
                className={cn(
                  'flex w-full items-center gap-2.5 overflow-hidden rounded-md px-2.5 py-2 text-left text-xs transition-colors',
                  chat.id === activeChatId
                    ? 'bg-primary/10 text-indigo-700'
                    : 'hover:bg-muted text-[rgb(var(--ink-2))]',
                )}
              >
                <span className="min-w-0 flex-1 truncate">{chat.title}</span>
                <span className="text-muted-foreground shrink-0 text-[10.5px]">{chat.when}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </aside>
  );
}
